import Modal from "components/base/modal/Modal";
import { ConfigContextV2, ModalsContextV2 } from "contexts";
import { createContext, FC, JSX, useCallback, useContext, useMemo, useState } from "react";
import { DropdownOption, usePoliciesPagesConfig, useRuntimeConfig } from "@finbackoffice/site-core";
import { useGetCurrentFeedType } from "hooks";
import { FeedScopeEnum } from "utils/constants";
import { usePathname } from "next/navigation";
import { ModalTypes, RouteNames } from "utils/constants";
import { getLanguageNativeName, PolicyPagesEnum, IPolicyPagesConfig } from "@finbackoffice/fe-core";
import ActiveLink from "components/base/active-link/ActiveLink";
import Translate from "components/base/translate/Translate";
import MainSidebar from "./main/MainSidebar";
import AdvancedSidebar from "./advanced/AdvancedSidebar";
import styles from "./sidebar.module.sass";

export const routeTopLeagues = `/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.TOP}`;
export const routeUpcoming = `/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.UPCOMING}`;
export const routeFavorites = `/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.FAVORITES}`;

type ViewType = "sport" | "live" | "casino" | "";

type ISidebarContext = {
    viewType: ViewType;
    languages: DropdownOption[];
    oddsFormats: DropdownOption[];
    renderPoliciesLink: (item: IPolicyPagesConfig) => JSX.Element | null;
};

export const SidebarContext = createContext<ISidebarContext>(null as any);

const Sidebar: FC = () => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { enabledPolicySportsbookConfig } = usePoliciesPagesConfig();
    const pathname = usePathname();
    const feed = useGetCurrentFeedType();

    const { siteSupportedOddsFormat, siteDirection } = useContext(ConfigContextV2);
    const { advancedSidebarModalRef } = useContext(ModalsContextV2);

    const [animateAdvanced, setAnimateAdvanced] = useState(false);

    const isCasino =
        pathname.includes(`${RouteNames.CASINO}/${RouteNames.LIVE_CASINO}`) ||
        pathname.includes(`${RouteNames.CASINO}/${RouteNames.SLOTS}`);

    const viewType: ViewType = useMemo(() => {
        if (feed === FeedScopeEnum.prematch) {
            return "sport";
        } else if (feed === FeedScopeEnum.live) {
            return "live";
        } else if (isCasino) {
            return "casino";
        } else {
            return "";
        }
    }, [feed, isCasino]);

    const languages: DropdownOption[] = useMemo(
        () =>
            COMMON_SITE_CONFIGS.activeLocales?.map((lang) => {
                return {
                    value: lang,
                    label: getLanguageNativeName(lang),
                };
            }) ?? [],
        [COMMON_SITE_CONFIGS.activeLocales],
    );

    const oddsFormats: DropdownOption[] = useMemo(
        () =>
            (siteSupportedOddsFormat ?? []).map((format) => ({
                value: format,
                label: `oddsFormats_${format}`,
            })),
        [siteSupportedOddsFormat],
    );

    const renderPoliciesLink = useCallback(
        (item: IPolicyPagesConfig) => {
            let content;
            switch (item.name) {
                case PolicyPagesEnum.Privacy:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.PRIVACY}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_privacy" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Terms:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.TERMS}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_terms" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Sports:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.SPORTSBOOK}/${enabledPolicySportsbookConfig?.[0]?.name.toLowerCase().replace(/_/g, "-")}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_rules" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Casino:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.CASINO}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_casino" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Payments:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.PAYMENTS}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_payments" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.ResponsibleGaming:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.RESPONSIBLE_GAMING}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_responsible_gaming" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Cookies:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.COOKIES}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_cookies" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.SelfExclusion:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.SELF_EXCLUSION}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_self_exclusion" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.DisputeResolution:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.DISPUTE_RESOLUTION}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_dispute_resolution" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.AntiMoneyLaundering:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.ANTI_MONEY_LAUNDERING}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_anti_money_laundering" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.FairnessRngTesting:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.FAIRNESS_RNG_TESTING}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_fairness_rng_testing" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Kyc:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.KYC}`}
                            activeClassName={styles.selected}>
                            <Translate tid="pageName_policies_kyc" />
                        </ActiveLink>
                    );
                    break;
                default:
                    content = null;
            }
            return content;
        },
        [enabledPolicySportsbookConfig],
    );

    const onAnimationEndHandler = useCallback((visible: boolean) => {
        setAnimateAdvanced(visible);
    }, []);

    const value = useMemo(
        () => ({
            viewType,
            languages,
            oddsFormats,
            renderPoliciesLink,
        }),
        [languages, oddsFormats, renderPoliciesLink, viewType],
    );

    return (
        <SidebarContext.Provider value={value}>
            <MainSidebar animate={!animateAdvanced} />
            <Modal
                ref={advancedSidebarModalRef}
                type={ModalTypes.ADVANCED_SIDEBAR}
                styleClass={styles.advancedSidebarModal}
                animateVariant={siteDirection === "rtl" ? "right" : "left"}
                onAnimationEnd={onAnimationEndHandler}>
                <AdvancedSidebar animate={animateAdvanced} />
            </Modal>
        </SidebarContext.Provider>
    );
};

export default Sidebar;
